import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/Navbar';
import { LayoutDashboard, Users, Package, Tag, Shield, Palette, TrendingUp } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AdminDashboard = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [stats, setStats] = useState({
    pendingSellers: 0,
    totalProducts: 0,
    activeCoupons: 0,
    totalUsers: 0
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [pendingSellersRes, productsRes, couponsRes, usersRes] = await Promise.all([
          axios.get(`${API_URL}/admin/users`, { withCredentials: true }),
          axios.get(`${API_URL}/admin/products`, { withCredentials: true }),
          axios.get(`${API_URL}/admin/coupons`, { withCredentials: true }),
          axios.get(`${API_URL}/admin/all-users`, { withCredentials: true })
        ]);

        setStats({
          pendingSellers: pendingSellersRes.data.length,
          totalProducts: productsRes.data.length,
          activeCoupons: couponsRes.data.filter(c => c.active).length,
          totalUsers: usersRes.data.length
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };

    fetchStats();
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="admin-dashboard-title">
            Painel Administrativo
          </h1>
          <p className="text-lg text-gray-600">Bem-vindo, {user?.name}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="stat-users">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Usuários Totais</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalUsers}</p>
              </div>
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="stat-pending">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Vendedores Pendentes</p>
                <p className="text-3xl font-bold text-amber-600 mt-2">{stats.pendingSellers}</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="stat-products">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Produtos</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalProducts}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Package className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="stat-coupons">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Cupons Ativos</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.activeCoupons}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Tag className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Ações Rápidas</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link
              to="/admin/metrics"
              className={`p-6 rounded-lg border-2 transition-all hover:shadow-md ${
                isActive('/admin/metrics')
                  ? 'border-emerald-600 bg-emerald-50'
                  : 'border-gray-200 hover:border-emerald-300'
              }`}
              data-testid="admin-metrics-link"
            >
              <TrendingUp className="w-8 h-8 text-emerald-600 mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">Métricas de Pagamento</h3>
              <p className="text-sm text-gray-600">Receita, MRR, conversão e planos mais vendidos</p>
            </Link>

            <Link
              to="/admin/home-editor"
              className={`p-6 rounded-lg border-2 transition-all hover:shadow-md ${
                isActive('/admin/home-editor')
                  ? 'border-pink-600 bg-pink-50'
                  : 'border-gray-200 hover:border-pink-300'
              }`}
              data-testid="admin-home-editor-link"
            >
              <Palette className="w-8 h-8 text-pink-600 mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">Editor Home</h3>
              <p className="text-sm text-gray-600">Personalizar página inicial</p>
            </Link>

            <Link
              to="/admin/manage-users"
              className={`p-6 rounded-lg border-2 transition-all hover:shadow-md ${
                isActive('/admin/manage-users')
                  ? 'border-amber-600 bg-amber-50'
                  : 'border-gray-200 hover:border-amber-300'
              }`}
              data-testid="admin-manage-users-link"
            >
              <Shield className="w-8 h-8 text-amber-600 mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">Controle de Planos</h3>
              <p className="text-sm text-gray-600">Alterar planos e créditos</p>
            </Link>

            <Link
              to="/admin/users"
              className={`p-6 rounded-lg border-2 transition-all hover:shadow-md ${
                isActive('/admin/users')
                  ? 'border-indigo-600 bg-indigo-50'
                  : 'border-gray-200 hover:border-indigo-300'
              }`}
              data-testid="admin-users-link"
            >
              <Users className="w-8 h-8 text-indigo-600 mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">Aprovar Vendedores</h3>
              <p className="text-sm text-gray-600">Vendedores pendentes</p>
            </Link>

            <Link
              to="/admin/products"
              className={`p-6 rounded-lg border-2 transition-all hover:shadow-md ${
                isActive('/admin/products')
                  ? 'border-green-600 bg-green-50'
                  : 'border-gray-200 hover:border-green-300'
              }`}
              data-testid="admin-products-link"
            >
              <Package className="w-8 h-8 text-green-600 mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">Gerenciar Produtos</h3>
              <p className="text-sm text-gray-600">Adicionar e editar</p>
            </Link>

            <Link
              to="/admin/coupons"
              className={`p-6 rounded-lg border-2 transition-all hover:shadow-md ${
                isActive('/admin/coupons')
                  ? 'border-purple-600 bg-purple-50'
                  : 'border-gray-200 hover:border-purple-300'
              }`}
              data-testid="admin-coupons-link"
            >
              <Tag className="w-8 h-8 text-purple-600 mb-3" />
              <h3 className="font-semibold text-gray-900 mb-1">Gerenciar Cupons</h3>
              <p className="text-sm text-gray-600">Criar descontos</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};